import PropTypes from 'prop-types';
import React, { cloneElement, useState, useEffect } from 'react';
import { withRouter } from 'utils/withRouter';
import { useSelector } from 'react-redux';
import { compose } from 'redux';
import { injectIntl } from 'react-intl';
import { useSpring, animated } from '@react-spring/web';
import Sticker from 'react-stickyfill';
import styled, { css } from 'styled-components';
import ErrorHandler from 'common/error/components/error-handler';
import { types } from 'utils/mappings';
import { media } from 'ui';
import { OnPhone } from 'ui/media';
import MenuItem from './menu-item';
import PermissionCheck from '../../features/permission-check';
import { announcementBannerHeight } from '../../../utils/app-configs';
import settingsConfig from '../../../settings/settings-config';

const fixedLinks = [
  { id: 'home', translationKey: 'nav.HOME', uri: '/home' },
  { id: 'tickets', translationKey: 'nav.TICKETS', uri: '/tickets', permissions: ['ticketmanage'] },
  { id: 'worklists', translationKey: 'nav.WORKLISTS', uri: '/worklists' },
  {
    id: 'planningboard',
    translationKey: 'nav.PLANNINGBOARD',
    uri: '/planning-board/serviceplanning',
    permissions: ['planboardread'],
  },
  {
    id: 'settings', translationKey: 'nav.SETTINGS', uri: '/settings',
    permissions: Object.values(settingsConfig.permissions),
  },
];

const getAllLinks = (params) => {
  const {
    navigationParents,
    navigationChilds,
    navigationSelf,
    location,
    loading,
    forceActive,
  } = params;
  const allLinks = [...fixedLinks];

  if (!loading) {
    allLinks.push(...navigationParents);
    if (navigationSelf && navigationSelf.id) {
      const ownLink = {
        ...navigationSelf,
        uri: !navigationSelf.uri || forceActive ? location.pathname : `/${navigationSelf.uri}`,
      };
      allLinks.push(ownLink);
    }
    allLinks.push(...navigationChilds);
  } else {
    allLinks.push({
      uri: location.pathname,
      translationKey: 'nav.LOADING',
    });
  }
  return allLinks;
};

export const MainMenuLinks = styled.ul`
  display: ${p => !p.isFullScreen ? 'flex' : 'none'};
  justify-content: flex-start;
  align-items: center;
  background-color: #fff;
  border-bottom: 1px solid ${p => p.theme.colors.border};
  margin: 0 -20px;
  padding: 5px 20px 0;
  z-index: 9;
  ${media.tablet`
    flex-direction: column;
    align-items: flex-start;
    justify-content: center;
    padding: 0;
    display: ${p => p.visible ? 'flex' : 'none'};
  `};
`;

const AnimatedDiv = styled(animated.div)`
  position: sticky;
  top: ${p => p.isFullScreen ? '0px' : '55px'};
`;

const MainMenuWrapper = styled.div`
  height: 54px;

  ${props =>
    props.isFullScreen &&
    css`
      height: 22px;
      background: rgb(0, 152, 204);
      margin-left: -20px;
      margin-right: -20px;
    `}
`;

const MainMenu = (props) => {
  const {
    children,
    location,
    intl,
    error,
    navigationParents,
    navigationChilds,
    navigationSelf,
    loading,
    expand,
    actionButtons,
    forceActive,
    menuVisible,
    setMenuVisible,
    planningBoardFullScreen,
  } = props;
  const [isLoading, setIsLoading] = useState([]);
  const { hasAnnouncementBanner } = useSelector(state => state.user);
  const {
    isLoadingKpisLandingZone,
    isLoadingCampaignsLandingZone,
  } = useSelector(state => state.landingZone);
  const {
    isLoadingAnnouncements,
  } = useSelector(state => state.announcements);

  useEffect(() => {
    if (
      isLoadingKpisLandingZone ||
      isLoadingCampaignsLandingZone ||
      isLoadingAnnouncements
    ) {
      setIsLoading(s => s?.find(l => l === 'home') ? s : [...s, 'home']);
    } else {
      setIsLoading(s => s?.filter(a => a !== 'home'));
    }
  }, [
    isLoadingKpisLandingZone,
    isLoadingCampaignsLandingZone,
  ]);

  const allLinks = getAllLinks({
    navigationParents,
    navigationChilds,
    navigationSelf,
    location,
    loading,
    forceActive,
  });

  const translateYpx = -55;
  const updatedTranslateY = hasAnnouncementBanner ?
    `${translateYpx + announcementBannerHeight}px` :
    `${translateYpx}px`;

  const originalTranslateY = hasAnnouncementBanner && window.pageYOffset > 10 ?
    `${announcementBannerHeight}px` :
    '0px';

  const menuProps = useSpring({
    boxShadow: menuVisible ? '0px 0px 0px 0px gray' : '0px 15px 20px -7px gray',
    transform: menuVisible ?
      `translateY(${originalTranslateY})` :
      `translateY(${updatedTranslateY})`,
    opacity: menuVisible ? 1 : 0.7,
    zIndex: menuVisible ? 90 : 9,
    config: { duration: 400 },
  });

  /* eslint-disable no-undefined */
  return (
    <>
      <ErrorHandler
        type='notification'
        error={error}
        isFullScreen={planningBoardFullScreen}
      />
      <Sticker>
        <AnimatedDiv
          style={menuProps}
          isFullScreen={planningBoardFullScreen}
          onMouseEnter={() => window.pageYOffset > 10 && setMenuVisible(true)}
          onMouseLeave={() => window.pageYOffset > 10 && setMenuVisible(false)}
        >
          <MainMenuWrapper isFullScreen={planningBoardFullScreen}>
            <MainMenuLinks visible={expand} isFullScreen={planningBoardFullScreen}>
              {allLinks?.map(item =>
                <PermissionCheck
                  key={item.uri}
                  permissionsList={!item.permissions?.length ? ['alwaysTrue'] : item.permissions}
                >
                  <MenuItem
                    type={intl.formatMessage({
                      id: item.translationKey || types[item.type]?.displayTextTranslationKey,
                    })}
                    link={item.uri}
                    title={item.title}
                    toolTip={[
                      {
                        label: intl.formatMessage({ id: 'common.labels.navtooltip.Title' }),
                        value: item?.title,
                      },
                      ...item?.toolTipData || [],
                    ]}
                    hasType={!!item.type}
                    itemType={item.type}
                    loading={isLoading?.find(l => l === item.id)}
                    translationKey={
                      item.translationKey ||
                      types[item.type]?.displayTextTranslationKey
                    }
                  />
                </PermissionCheck>,
              )}
            </MainMenuLinks>
          </MainMenuWrapper>
        </AnimatedDiv>
      </Sticker>
      {children && cloneElement(children, { setMenuVisible, menuVisible, expand, actionButtons })}
      <OnPhone>{actionButtons}</OnPhone>
    </>
  );
};
/* eslint-enable no-undefined */

MainMenu.propTypes = {
  navigationParents: PropTypes.array.isRequired,
  navigationChilds: PropTypes.array.isRequired,
  navigationSelf: PropTypes.object.isRequired,
  children: PropTypes.node,
  actionButtons: PropTypes.node,
  location: PropTypes.object,
  error: PropTypes.object,
  loading: PropTypes.bool,
  expand: PropTypes.bool,
  intl: PropTypes.object,
  forceActive: PropTypes.bool,
  menuVisible: PropTypes.bool,
  setMenuVisible: PropTypes.func,
  planningBoardFullScreen: PropTypes.bool,
};

export default compose(withRouter, injectIntl)(MainMenu);
